import type { Component } from 'vue'
import type { RouteRecordRaw } from 'vue-router'
import {
  Home,
  FileEdit,
  FolderGit,
  GraduationCap,
  Compass,
  Library,
  Settings2,
  LogIn,
  UserPlus,
} from 'lucide-vue-next'

/** Route visibility configuration */
export interface RouteAccess {
  /** Route requires authentication */
  requiresAuth: boolean
  /** Route is only for guests (non-authenticated users) */
  guestOnly?: boolean
  /** Route requires teacher role */
  requiresTeacher?: boolean
  /** Route is accessible to everyone regardless of auth state */
  public?: boolean
}

/** Breadcrumb item configuration */
export interface BreadcrumbItem {
  /** Display title */
  title: string
  /** Route name to navigate to (omit for current page) */
  to?: string
}

/** Route metadata for navigation */
export interface RouteNavMeta {
  /** Display title */
  title: string
  /** Lucide icon component */
  icon?: Component
  /** Show in navigation menu */
  showInNav: boolean
  /** Order in navigation (lower = higher) */
  order?: number
  /** Breadcrumb trail (parent items leading to this route) */
  breadcrumbs?: BreadcrumbItem[]
}

/** Complete route configuration */
export interface AppRouteConfig {
  path: string
  name: string
  component: () => Promise<Component>
  access: RouteAccess
  nav: RouteNavMeta
}

/**
 * Application route definitions
 * Single source of truth for all routes
 */
export const appRoutes: AppRouteConfig[] = [
  // Auth routes (guest only)
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/auth/login/LoginView.vue'),
    access: {
      requiresAuth: false,
      guestOnly: true,
    },
    nav: {
      title: 'Iniciar sesión',
      icon: LogIn,
      showInNav: false,
    },
  },
  {
    path: '/registro',
    name: 'SignUp',
    component: () => import('@/views/auth/signup/SignUpView.vue'),
    access: {
      requiresAuth: false,
      guestOnly: true,
    },
    nav: {
      title: 'Registrarse',
      icon: UserPlus,
      showInNav: false,
    },
  },

  // Main app routes (authenticated)
  {
    path: '/',
    name: 'Home',
    component: () => import('@/views/home/HomeView.vue'),
    access: {
      requiresAuth: true,
    },
    nav: {
      title: 'Inicio',
      icon: Home,
      showInNav: true,
      order: 1,
    },
  },
  {
    path: '/editor',
    name: 'Editor',
    component: () => import('@/views/editor/EditorView.vue'),
    access: {
      requiresAuth: false,
      public: true,
    },
    nav: {
      title: 'Editor',
      icon: FileEdit,
      showInNav: true,
      order: 2,
    },
  },
  {
    path: '/repositorio',
    name: 'Repository',
    component: () => import('@/views/repository/RepositoryView.vue'),
    access: {
      requiresAuth: true,
    },
    nav: {
      title: 'Repositorio',
      icon: FolderGit,
      showInNav: true,
      order: 3,
      // Example breadcrumbs
      // breadcrumbs: [{ title: 'Inicio', to: 'Home' }],
    },
  },
  {
    path: '/aulas',
    name: 'Classrooms',
    component: () => import('@/views/classrooms/ClassroomsView.vue'),
    access: {
      requiresAuth: true,
      requiresTeacher: true,
    },
    nav: {
      title: 'Aulas',
      icon: GraduationCap,
      showInNav: true,
      order: 4,
    },
  },
  {
    path: '/explorar',
    name: 'Explore',
    component: () => import('@/views/explore/ExploreView.vue'),
    access: {
      requiresAuth: true,
    },
    nav: {
      title: 'Explorar',
      icon: Compass,
      showInNav: true,
      order: 5,
    },
  },
  {
    path: '/recursos',
    name: 'Resources',
    component: () => import('@/views/resources/ResourcesView.vue'),
    access: {
      requiresAuth: true,
    },
    nav: {
      title: 'Recursos',
      icon: Library,
      showInNav: true,
      order: 6,
    },
  },
  {
    path: '/ajustes',
    name: 'Settings',
    component: () => import('@/views/settings/SettingsView.vue'),
    access: {
      requiresAuth: true,
    },
    nav: {
      title: 'Ajustes',
      icon: Settings2,
      showInNav: true,
      order: 7,
    },
  },
]

/**
 * Convert app routes to vue-router format
 */
export function toRouterRoutes(): RouteRecordRaw[] {
  return appRoutes.map((route) => ({
    path: route.path,
    name: route.name,
    component: route.component,
    meta: {
      title: route.nav.title,
      breadcrumbs: route.nav.breadcrumbs,
      ...route.access,
    },
  }))
}
